// modules/reportePagos/pages/ReportePagosTipoBancoPage.jsx
// Migracion de busqueda.php (pestañas "Mostrar Mes"/"Mostrar Año"/Reporte del
// Año -> dos/dosanio/cua): cuanto se cobro por Tipo de Ingreso y Banco de
// depósito. tipo_pago=1 marca que ese cobro puntual se recibio en efectivo
// dólares (aunque el monto siempre queda guardado en bolivianos); por eso
// cada casillero banco+moneda-de-cobro muestra tanto el monto en Bs como su
// equivalente en $us (con el tipo de cambio de ese pago puntual).
//
// Se le arreglaron dos cosas del reporte viejo:
//   - La consulta de fecha usaba SUBSTRING/SUBSTRING_INDEX sobre el string;
//     ahora usa YEAR()/MONTH() nativos.
//   - Los bancos de depósito estaban hardcodeados (BNB/AVD/BMSCZ/Union, y
//     cualquier otro banco caia en un "BCP" generico). Ahora la lista de
//     bancos sale en vivo de la tabla `banco`, así no se mezclan cobros de
//     bancos distintos si se habilita uno nuevo.
//   - El detalle mensual por banco (antes "Reporte del Año") solo distinguía
//     Ingreso vs Franquicia — Intercambio de Servicio y Castigo quedaban mal
//     clasificados como "Ingreso". Ahora se pide el Tipo de Ingreso como
//     filtro, así esa mezcla ya no puede pasar.
import React, { useMemo, useState } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  Tabs, Tab, Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { getReporteTipoBanco, getReporteMensualPorBanco } from "../services/reportePagosService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const bodySx = { "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } };

const SUCURSALES = ["Todo", "La Paz", "Cochabamba", "Santa Cruz"];
const MESES = [
  { value: "Todo", label: "Todo (año completo)" },
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];
const TIPOS_INGRESO = [
  { value: "1", label: "Ingreso" },
  { value: "2", label: "Franquicia" },
  { value: "3", label: "Intercambio de Servicios" },
  { value: "4", label: "Castigo" },
];
const NOMBRES_MES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i);

const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const sumaColumnas = (columnas, unidad) =>
  Object.values(columnas || {}).reduce((acc, c) => acc + (c[unidad] || 0), 0);

// Igual que ReportePagosPorServicioPage (colorFilaServicio) pero por banco en vez de
// servicio, y aplicado a columnas en vez de filas — un color por banco (mismo para
// sus dos sub-columnas Bs./$us.), como en el reporte legado. Como la lista de bancos
// sale en vivo de la BD (no es un enum fijo como los servicios), se cicla una paleta
// por indice de aparicion en vez de mapear por nombre.
const PALETA_BANCO_CLARO = ["#e9e3ab", "#f3c9e3", "#c6e8b3", "#f6c98a", "#a9d6e8", "#d8c9f0", "#f2e6a3", "#b7e2e6"];
const PALETA_BANCO_OSCURO = ["#544f28", "#54324a", "#2f4a29", "#5c4322", "#264a54", "#3d2f54", "#524c26", "#264749"];

const ReportePagosTipoBancoPage = () => {
  const [tab, setTab] = useState(0);
  const [sucursal, setSucursal] = useState("Todo");
  const [year, setYear] = useState(anioActual);

  // --- Tab 0: Resumen (dos / dosanio) ---
  const [mes, setMes] = useState("Todo");
  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [errorResumen, setErrorResumen] = useState("");
  const [buscadoResumen, setBuscadoResumen] = useState(false);

  // c.key (banco + moneda-de-cobro, ej. "3_bs"/"3_us" — un mismo banco puede
  // aparecer dos veces, una por lo cobrado en Bs y otra por lo cobrado en
  // efectivo $us) -> indice de paleta, en orden de aparicion. Cada grupo de
  // columnas (Bs./$us. equivalente) comparte un solo color, igual que el
  // reporte legado coloreaba cada bloque de banco.
  const colorPorColumna = useMemo(() => {
    const keys = (resumen?.columnas || []).map((c) => c.key);
    return Object.fromEntries(keys.map((key, idx) => [key, idx % PALETA_BANCO_CLARO.length]));
  }, [resumen]);

  const colorColumnaBanco = (key) => (theme) => {
    const idx = colorPorColumna[key];
    if (idx == null) return "inherit";
    return theme.palette.mode === "dark" ? PALETA_BANCO_OSCURO[idx] : PALETA_BANCO_CLARO[idx];
  };

  const buscarResumen = async () => {
    setLoadingResumen(true);
    setErrorResumen("");
    try {
      const { data } = await getReporteTipoBanco({ sucursal, year, mes });
      setResumen(data);
      setBuscadoResumen(true);
    } catch (e) {
      setResumen(null);
      setErrorResumen(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoadingResumen(false);
    }
  };

  // --- Tab 1: Detalle Mensual (cua) ---
  const [tipoIngreso, setTipoIngreso] = useState("1");
  const [mensual, setMensual] = useState(null);
  const [loadingMensual, setLoadingMensual] = useState(false);
  const [errorMensual, setErrorMensual] = useState("");
  const [buscadoMensual, setBuscadoMensual] = useState(false);

  // Mismo criterio que colorPorColumna/colorColumnaBanco de la pestaña Resumen,
  // pero por fila (acá cada banco+moneda-de-cobro es una fila, no una columna) —
  // se calcula aparte porque sale de `mensual`, no de `resumen`, aunque ambos
  // usan el mismo orden de bancos (columnasPorBancos) asi que los colores
  // coinciden entre pestañas.
  const colorPorFilaBanco = useMemo(() => {
    const keys = (mensual?.filas || []).map((f) => f.key);
    return Object.fromEntries(keys.map((key, idx) => [key, idx % PALETA_BANCO_CLARO.length]));
  }, [mensual]);

  const colorFilaBanco = (key) => (theme) => {
    const idx = colorPorFilaBanco[key];
    if (idx == null) return "inherit";
    return theme.palette.mode === "dark" ? PALETA_BANCO_OSCURO[idx] : PALETA_BANCO_CLARO[idx];
  };

  const totalMensualBanco = useMemo(() => {
    if (!mensual) return null;
    const meses = Array.from({ length: 12 }, () => ({ bs: 0, usd: 0 }));
    const total = { bs: 0, usd: 0 };
    for (const fila of mensual.filas) {
      fila.meses.forEach((m, idx) => {
        meses[idx].bs += m.bs;
        meses[idx].usd += m.usd;
      });
      total.bs += fila.total.bs;
      total.usd += fila.total.usd;
    }
    return { meses, total };
  }, [mensual]);

  const buscarMensual = async () => {
    setLoadingMensual(true);
    setErrorMensual("");
    try {
      const { data } = await getReporteMensualPorBanco({ sucursal, year, tipoIngreso });
      setMensual(data);
      setBuscadoMensual(true);
    } catch (e) {
      setMensual(null);
      setErrorMensual(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoadingMensual(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Reporte por Tipo, Moneda y Banco"
        subtitle="Cuánto se cobró por tipo de ingreso y banco de depósito"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, fontWeight: 700, textTransform: "none" } }}
          >
            <Tab label="Resumen" />
            <Tab label="Detalle Mensual" />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              <TextField
                select size="small" label="Sucursal" sx={{ ...compactFieldSx, width: 150 }}
                value={sucursal} onChange={(e) => setSucursal(e.target.value)}
              >
                {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Mes" sx={{ ...compactFieldSx, width: 190 }}
                value={mes} onChange={(e) => setMes(e.target.value)}
              >
                {MESES.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.85rem" }}>{m.label}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Año" sx={{ ...compactFieldSx, width: 120 }}
                value={year} onChange={(e) => setYear(Number(e.target.value))}
              >
                {ANIOS.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>)}
              </TextField>
              <Button
                size="small" variant="contained"
                startIcon={loadingResumen ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                onClick={buscarResumen} disabled={loadingResumen} sx={{ minHeight: 32 }}
              >
                {loadingResumen ? "Buscando..." : "Buscar"}
              </Button>
            </Box>

            {errorResumen && <Alert severity="error" sx={{ mb: 2 }}>{errorResumen}</Alert>}

            {!buscadoResumen ? (
              <Alert severity="info">Elige sucursal, mes y año, y presiona Buscar.</Alert>
            ) : (
              <>
                <Paper variant="outlined">
                  <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                    <Table size="small" sx={bodySx}>
                      <TableHead>
                        <TableRow>
                          <StyledHeaderCell rowSpan={2}>Tipo de Ingreso</StyledHeaderCell>
                          {resumen.columnas.map((c) => (
                            <StyledHeaderCell key={c.key} align="center" colSpan={2} sx={{ backgroundColor: colorColumnaBanco(c.key) }}>
                              {c.banco}{c.moneda === "us" ? " (cobrado en $us)" : ""}
                            </StyledHeaderCell>
                          ))}
                          <StyledHeaderCell align="center" colSpan={2}>Total</StyledHeaderCell>
                        </TableRow>
                        <TableRow>
                          {resumen.columnas.map((c) => (
                            <React.Fragment key={c.key}>
                              <StyledHeaderCell align="right" sx={{ backgroundColor: colorColumnaBanco(c.key) }}>Bs.</StyledHeaderCell>
                              <StyledHeaderCell align="right" sx={{ backgroundColor: colorColumnaBanco(c.key) }}>$us.</StyledHeaderCell>
                            </React.Fragment>
                          ))}
                          <StyledHeaderCell align="right">Bs.</StyledHeaderCell>
                          <StyledHeaderCell align="right">$us.</StyledHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resumen.filas.map((fila) => (
                          <TableRow key={fila.tipo} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{fila.label}</TableCell>
                            {resumen.columnas.map((c) => {
                              const celda = fila.columnas[c.key] || { bs: 0, usd: 0 };
                              return (
                                <React.Fragment key={c.key}>
                                  <TableCell align="right" sx={{ backgroundColor: colorColumnaBanco(c.key) }}>{formatMoneda(celda.bs)}</TableCell>
                                  <TableCell align="right" sx={{ backgroundColor: colorColumnaBanco(c.key) }}>{formatMoneda(celda.usd)}</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneda(sumaColumnas(fila.columnas, "bs"))}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneda(sumaColumnas(fila.columnas, "usd"))}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          {resumen.columnas.map((c) => {
                            const t = resumen.totalPorColumna[c.key] || { bs: 0, usd: 0 };
                            return (
                              <React.Fragment key={c.key}>
                                <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: colorColumnaBanco(c.key) }}>{formatMoneda(t.bs)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: colorColumnaBanco(c.key) }}>{formatMoneda(t.usd)}</TableCell>
                              </React.Fragment>
                            );
                          })}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(resumen.totales.generalBs)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(resumen.totales.generalUsdEquiv)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Stack direction="row" spacing={3} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <Typography variant="body2">Total cobrado en efectivo Bolivianos: <b>{formatMoneda(resumen.totales.depositadoBs)}</b></Typography>
                  <Typography variant="body2">Total cobrado en efectivo Dólares: <b>{formatMoneda(resumen.totales.depositadoUsd)}</b></Typography>
                </Stack>
              </>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              <TextField
                select size="small" label="Sucursal" sx={{ ...compactFieldSx, width: 150 }}
                value={sucursal} onChange={(e) => setSucursal(e.target.value)}
              >
                {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Tipo de Ingreso" sx={{ ...compactFieldSx, width: 210 }}
                value={tipoIngreso} onChange={(e) => setTipoIngreso(e.target.value)}
              >
                {TIPOS_INGRESO.map((t) => <MenuItem key={t.value} value={t.value} sx={{ fontSize: "0.85rem" }}>{t.label}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Año" sx={{ ...compactFieldSx, width: 120 }}
                value={year} onChange={(e) => setYear(Number(e.target.value))}
              >
                {ANIOS.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>)}
              </TextField>
              <Button
                size="small" variant="contained"
                startIcon={loadingMensual ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                onClick={buscarMensual} disabled={loadingMensual} sx={{ minHeight: 32 }}
              >
                {loadingMensual ? "Buscando..." : "Buscar"}
              </Button>
            </Box>

            {errorMensual && <Alert severity="error" sx={{ mb: 2 }}>{errorMensual}</Alert>}

            {!buscadoMensual ? (
              <Alert severity="info">Elige sucursal, tipo de ingreso y año, y presiona Buscar.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                  <Table size="small" sx={bodySx}>
                    <TableHead>
                      <TableRow>
                        <StyledHeaderCell>Banco</StyledHeaderCell>
                        <StyledHeaderCell>Moneda</StyledHeaderCell>
                        {NOMBRES_MES_CORTOS.map((m) => (
                          <StyledHeaderCell key={m} align="right">{m}</StyledHeaderCell>
                        ))}
                        <StyledHeaderCell align="right">Total</StyledHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mensual.filas.map((fila) => {
                        const colorFila = colorFilaBanco(fila.key);
                        return (
                          <React.Fragment key={fila.key}>
                            <TableRow sx={{ backgroundColor: colorFila }}>
                              <TableCell rowSpan={2} sx={{ fontWeight: 600, backgroundColor: colorFila }}>{fila.banco}</TableCell>
                              <TableCell>Bs.</TableCell>
                              {fila.meses.map((m, idx) => (
                                <TableCell key={idx} align="right">{formatMoneda(m.bs)}</TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneda(fila.total.bs)}</TableCell>
                            </TableRow>
                            <TableRow sx={{ backgroundColor: colorFila }}>
                              <TableCell>$us.</TableCell>
                              {fila.meses.map((m, idx) => (
                                <TableCell key={idx} align="right">{formatMoneda(m.usd)}</TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneda(fila.total.usd)}</TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                      {totalMensualBanco && (
                        <>
                          <TableRow sx={{ backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[700] : t.palette.grey[300]) }}>
                            <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>Total Mes</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Bs.</TableCell>
                            {totalMensualBanco.meses.map((m, idx) => (
                              <TableCell key={idx} align="right" sx={{ fontWeight: 700 }}>{formatMoneda(m.bs)}</TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(totalMensualBanco.total.bs)}</TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[700] : t.palette.grey[300]) }}>
                            <TableCell sx={{ fontWeight: 700 }}>$us.</TableCell>
                            {totalMensualBanco.meses.map((m, idx) => (
                              <TableCell key={idx} align="right" sx={{ fontWeight: 700 }}>{formatMoneda(m.usd)}</TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(totalMensualBanco.total.usd)}</TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReportePagosTipoBancoPage;
